// Collection reveal adapted from La Mere de Famille. Sort only the loaded cards;
// a sold-out product may appear before available products on a later server page.
(() => {
  const controllers = new Map();
  const mobile = window.matchMedia("(max-width: 740px)");
  const groupSize = () => mobile.matches ? 24 : 40;

  class CollectionReveal {
    constructor(facet) {
      this.facet = facet;
      this.generation = 0;
      this.rearm = () => this.observe();
      this.refresh();
    }

    items() {
      return this.list ? Array.from(this.list.querySelectorAll(".product-item")) : [];
    }

    pagination() {
      return this.facet.querySelector("#facet-main .pagination");
    }

    nextUrl() {
      return this.pagination()?.querySelector('a[rel="next"]')?.getAttribute("href");
    }

    hasMore() {
      return this.items().some(item => item.hidden) || Boolean(this.nextUrl());
    }

    detach() {
      this.observer?.disconnect();
      this.observer = null;
      window.removeEventListener("scroll", this.rearm);
    }

    cancel() {
      this.generation++;
      this.detach();
      this.request?.abort();
      this.request = null;
      window.clearTimeout(this.timer);
      this.timer = null;
      this.loading = false;
      this.list?.removeAttribute("aria-busy");
      this.sentinel?.remove();
      this.sentinel = null;
    }

    fallback() {
      this.cancel();
      this.failed = true;
      this.items().forEach(item => item.hidden = false);
      this.pagination()?.classList.remove("pagination--infinite-scroll");
    }

    refresh() {
      const main = this.facet.querySelector("#facet-main");
      const list = main?.querySelector(".product-list__inner");
      const enabled = Boolean(main?.hasAttribute("data-collection-infinite-reveal"));
      if (list === this.list && enabled === this.enabled) return;
      this.cancel();
      this.list = list;
      this.enabled = enabled;
      this.failed = false;
      this.limit = groupSize();
      this.appended = false;
      if (!list || !enabled || typeof IntersectionObserver !== "function") {
        this.fallback();
        return;
      }
      this.sort();
      this.visibility();
      this.observe();
    }

    sort() {
      // Moving only sold-out cards keeps the selected order inside each group.
      this.items().filter(item => item.classList.contains("product-item--sold-out"))
        .forEach(item => this.list.appendChild(item));
    }

    visibility() {
      this.items().forEach((item, index) => item.hidden = index >= this.limit);
    }

    observe() {
      this.detach();
      if (this.failed || !this.enabled || this.loading || !this.list?.isConnected) return;
      if (!this.hasMore()) {
        this.sentinel?.remove();
        this.sentinel = null;
        // Keep page links available on direct visits to the final server page.
        this.pagination()?.classList.toggle("pagination--infinite-scroll", this.appended);
        return;
      }
      if (!this.sentinel) {
        this.sentinel = document.createElement("div");
        this.sentinel.className = "collection-infinite-scroll-sentinel";
        this.sentinel.setAttribute("aria-hidden", "true");
        this.list.closest("product-list").after(this.sentinel);
      }
      this.pagination()?.classList.add("pagination--infinite-scroll");
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.loading) this.advance();
      }, { rootMargin: "200px 0px", threshold: 0 });
      this.observer.observe(this.sentinel);
    }

    finish() {
      this.loading = false;
      this.list?.removeAttribute("aria-busy");
      if (!this.hasMore()) {
        this.sentinel?.remove();
        this.sentinel = null;
        // Preserve previous-page navigation on a direct visit to the final page.
        this.pagination()?.classList.toggle("pagination--infinite-scroll", this.appended);
        return;
      }
      // Require another user scroll before observing again, even on short pages.
      window.addEventListener("scroll", this.rearm, { once: true, passive: true });
    }

    current(generation, list) {
      return generation === this.generation && list.isConnected &&
        list === this.facet.querySelector("#facet-main .product-list__inner");
    }

    async advance() {
      this.detach();
      this.loading = true;
      this.list.setAttribute("aria-busy", "true");
      const generation = this.generation;
      const list = this.list;
      if (this.items().some(item => item.hidden)) {
        this.timer = window.setTimeout(() => {
          this.timer = null;
          if (!this.current(generation, list)) return;
          this.limit += groupSize();
          this.visibility();
          this.finish();
        }, 300);
        return;
      }

      const next = this.nextUrl();
      if (!next) {
        this.finish();
        return;
      }
      this.request = new AbortController();
      this.facet.dispatchEvent(new CustomEvent("theme:loading:start", {
        bubbles: true, detail: { collectionReveal: true }
      }));
      let requestFailed = false;
      try {
        const url = new URL(next, window.location.origin);
        url.searchParams.set("section_id", this.facet.getAttribute("section-id"));
        const response = await fetch(url.toString(), { signal: this.request.signal });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const html = await response.text();
        if (!this.current(generation, list)) return;
        // Parse inertly: do not connect the response's product-list or its animations.
        const template = document.createElement("template");
        template.innerHTML = html;
        const incoming = template.content.querySelector("#facet-main .product-list__inner");
        if (!incoming || !incoming.querySelector(".product-item")) {
          throw new Error("Collection response has no product cards");
        }
        const pagination = this.pagination();
        const incomingPagination = template.content.querySelector("#facet-main .pagination");
        const items = Array.from(incoming.children);
        items.forEach(item => {
          // Focal's ProductList caches its initial cards; newly appended cards
          // must not wait for a reveal animation that will never include them.
          item.removeAttribute("reveal");
          item.style.removeProperty("opacity");
          item.hidden = true;
        });
        list.append(...items);
        this.appended = true;
        if (incomingPagination) {
          pagination.innerHTML = incomingPagination.innerHTML;
        } else {
          pagination?.remove();
        }
        this.sort();
        this.limit += groupSize();
        this.visibility();
        this.finish();
      } catch (error) {
        if (!this.current(generation, list)) return;
        requestFailed = true;
        console.error("[CollectionReveal]", error);
        this.fallback();
      } finally {
        // A filter owns its loading bar after cancellation of our request.
        if (generation === this.generation || requestFailed && list === this.list) {
          this.request = null;
          this.facet.dispatchEvent(new CustomEvent("theme:loading:end", {
            bubbles: true, detail: { collectionReveal: true }
          }));
        }
      }
    }
  }

  function refresh() {
    for (const [facet, controller] of controllers) {
      if (!facet.isConnected) {
        controller.cancel();
        controllers.delete(facet);
      }
    }
    document.querySelectorAll("product-facet[section-id]").forEach(facet => {
      if (!controllers.has(facet)) controllers.set(facet, new CollectionReveal(facet));
      else controllers.get(facet).refresh();
    });
  }

  // Capture before Focal starts replacing the filtered results.
  ["facet:criteria-changed", "pagination:page-changed", "facet:abort-loading"].forEach(name => {
    document.addEventListener(name, event => {
      const facet = event.target.closest?.("product-facet");
      controllers.get(facet)?.fallback();
    }, true);
  });
  document.addEventListener("theme:loading:end", event => {
    if (!event.detail?.collectionReveal) refresh();
  });
  document.addEventListener("shopify:section:load", refresh);
  document.addEventListener("shopify:section:unload", event => {
    for (const [facet, controller] of controllers) {
      if (event.target.contains(facet)) {
        const wasFetching = Boolean(controller.request);
        controller.fallback();
        if (wasFetching) facet.dispatchEvent(new CustomEvent("theme:loading:end", {
          bubbles: true, detail: { collectionReveal: true }
        }));
        controllers.delete(facet);
      }
    }
  });
  const resize = () => {
    refresh();
    for (const controller of controllers.values()) {
      if (!controller.failed && !controller.loading) controller.observe();
    }
  };
  if (typeof mobile.addEventListener === "function") mobile.addEventListener("change", resize);
  else mobile.addListener(resize);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", refresh, { once: true });
  else refresh();
})();
